import { Link } from "react-router-dom";
import { V2, timeAgo } from "./tokens";
import { Eyebrow, Pill, HairLine, ArticleMedia } from "./V2Shared";

const TYPE_PILL: Record<string, { label: string; color: string }> = {
  deep_dive: { label: "DEEP DIVE", color: V2.violet },
  weekly_recap: { label: "WEEKLY RECAP", color: V2.cyan2 },
  author: { label: "FIELD NOTE", color: V2.cyan2 },
  original: { label: "ORIGINAL", color: V2.green2 },
  analysis: { label: "ANALYSIS", color: V2.violet },
  opinion: { label: "OPINION", color: V2.cyan2 },
  interview: { label: "INTERVIEW", color: V2.violet },
};

/* Originals Magazine — one dominant authored idea + a quieter stack.
   Desktop (xl) approved; Phase 3: 768 (58:2) shows two secondary reads
   side-by-side, 390 (60:2) stacks them. Inventory-adaptive throughout. */
export const OriginalsMagazine = ({ data }: { data: any }) => {
  const originals = data?.originals || [];
  if (!originals.length) return null;
  const [featured, ...stack] = originals;
  return (
    <section data-testid="v2-originals" className="mt-[56px]">
      <Eyebrow color={V2.violet} size={11}>NEWSLAYER ORIGINALS</Eyebrow>
      <h2 className="nl-sg text-[28px] leading-[34px] md:text-[34px] md:leading-[40px] xl:text-[38px] xl:leading-[44px] font-medium mt-[12px]" style={{ color: V2.text }}>
        <span className="md:hidden">Original reporting. Exclusive analysis.</span>
        <span className="hidden md:inline">Original reporting should feel authored.</span>
      </h2>
      <p className="nl-geist text-[15px] leading-[23px] md:text-[16px] md:leading-[25px] mt-[10px] max-w-[900px]" style={{ color: V2.body }}>
        <span className="md:hidden">One dominant idea, quieter secondary reads and room for real visual journalism.</span>
        <span className="hidden md:inline xl:hidden">One dominant idea, quieter secondary reads and room for real photography, illustration and original data.</span>
        <span className="hidden xl:inline">A magazine spread with one dominant idea and quieter secondary reads.</span>
      </p>
      <HairLine className="mt-[18px]" />
      <div className="grid xl:grid-cols-[1fr_468px] gap-[32px] mt-[26px]">
        <Link to={`/article/${featured.slug}`} data-testid="v2-originals-featured"
          className="group relative block rounded-[24px] overflow-hidden min-h-[380px] md:min-h-[420px] xl:min-h-[526px]"
          style={{ border: `1px solid ${V2.border2}`, background: "linear-gradient(135deg,#120a26 0%,#071d28 100%)" }}>
          <div aria-hidden className="absolute -top-[80px] right-0 w-[460px] h-[460px] rounded-full"
            style={{ background: "radial-gradient(circle,#23d7ff2e,transparent 65%)" }} />
          <div aria-hidden className="absolute top-[160px] -left-[60px] w-[420px] h-[420px] rounded-full"
            style={{ background: "radial-gradient(circle,#8b5cff2e,transparent 65%)" }} />
          <div className="relative p-[20px] md:p-[28px] flex flex-col h-full">
            <Eyebrow color={V2.green2} size={11}>FEATURED ORIGINAL</Eyebrow>
            <ArticleMedia article={featured} className="mt-[20px] h-[160px] md:h-[170px] xl:h-[190px] rounded-[16px]" />
            <HairLine className="mt-[24px]" />
            <h3 className="nl-sg text-[24px] leading-[30px] md:text-[26px] md:leading-[32px] xl:text-[34px] xl:leading-[40px] font-medium mt-[20px] line-clamp-2 group-hover:opacity-90 transition-opacity duration-200"
              style={{ color: V2.text }}>
              {featured.title}
            </h3>
            {(featured.display_dek || featured.summary || featured.raw_excerpt) && (
              <p className="nl-geist text-[15px] md:text-[16px] leading-[24px] md:leading-[25px] mt-[14px] line-clamp-2" style={{ color: V2.body }}>
                {featured.display_dek || featured.summary || featured.raw_excerpt}
              </p>
            )}
            <p className="nl-mono text-[12px] leading-[16px] mt-auto pt-[18px]" style={{ color: V2.metaMuted }}>
              NewsLayer Originals{featured.read_minutes ? ` · ${featured.read_minutes} min read` : ""} · {timeAgo(featured.published_at)}
            </p>
          </div>
        </Link>
        {stack.length > 0 && (
          <div>
            <div className="hidden xl:block"><Eyebrow color={V2.cyan2} size={11}>THE MAGAZINE STACK</Eyebrow></div>
            <div className="relative mt-[4px] xl:mt-[16px] grid grid-cols-2 md:grid-cols-2 xl:grid-cols-1 gap-[16px] md:gap-[26px] md:gap-x-[40px] xl:gap-[30px]">
              <div aria-hidden className="hidden md:block xl:hidden absolute left-1/2 top-[4px] bottom-[4px] w-px"
                style={{ background: V2.border2 }} />
              {stack.slice(0, 3).map((a: any, i: number) => {
                const pill = TYPE_PILL[a.content_type] || TYPE_PILL.original;
                return (
                  <Link key={a.id} to={`/article/${a.slug}`} data-testid={`v2-originals-secondary-${i + 1}`}
                    className={`group block ${i === 2 ? "hidden xl:block" : ""}`}>
                    <span className="xl:hidden block nl-geist text-[10px] leading-[13px] md:text-[11px] md:leading-[14px] font-semibold uppercase tracking-[0.07em]"
                      style={{ color: pill.color }}>
                      {pill.label}
                    </span>
                    <span className="hidden xl:inline-flex"><Pill color={pill.color}>{pill.label}</Pill></span>
                    <h4 className="nl-sg text-[16px] leading-[21px] md:text-[23px] md:leading-[29px] xl:text-[27px] xl:leading-[33px] font-medium mt-[10px] md:mt-[12px] xl:mt-[14px] line-clamp-3 md:line-clamp-2 group-hover:opacity-90 transition-opacity duration-200"
                      style={{ color: V2.text }}>
                      {a.title}
                    </h4>
                    {(a.display_dek || a.summary || a.raw_excerpt) && (
                      <p className="hidden md:block nl-geist text-[13px] leading-[20px] xl:text-[14px] xl:leading-[21px] mt-[8px] line-clamp-2" style={{ color: V2.body }}>
                        {a.display_dek || a.summary || a.raw_excerpt}
                      </p>
                    )}
                    {a.read_minutes ? (
                      <p className="md:hidden nl-mono text-[8px] leading-[12px] mt-[8px] tracking-[0.08em]" style={{ color: V2.metaMuted }}>
                        {a.read_minutes} MIN READ
                      </p>
                    ) : null}
                    <HairLine className="mt-[18px] md:hidden xl:block" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
